<template>
  <div class="pc-select" data-cy="pc-select-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="pc-select !w-[80%] !max-w-[80%] !min-w-[715px] !translate-y-0"
        :style="{ top: getModalTop }"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ productCategoryFullName }}</SDialogTitle>
        </SDialogHeader>
        <div class="grid grid-cols-[repeat(24,minmax(0,1fr))]">
          <div class="col-span-4">
            <img
              v-if="pc"
              :src="productPicture"
              class="!h-[80px] !w-[80px] shrink-0 rounded object-cover"
            />
          </div>
          <div class="col-span-10"></div>
          <div class="col-span-10">
            <div class="grid grid-cols-[repeat(24,minmax(0,1fr))]">
              <div class="key col-span-12">{{ $t("field.availability") }}</div>
              <div class="value col-span-12" data-cy="pc-select-availability">
                {{ availabilities }}
                {{
                  isRetail ? $t("pcselect.left") : $t("pcselect.tickets-left")
                }}
              </div>
            </div>
            <div
              v-if="!isRetail"
              class="grid grid-cols-[repeat(24,minmax(0,1fr))]"
            >
              <div class="key col-span-12">
                {{ $t("field.start-date-time") }}
              </div>
              <div class="value col-span-12">
                {{ formatDateTime(ts?.startDateTime) }}
              </div>
            </div>
            <div
              v-if="!isRetail"
              class="grid grid-cols-[repeat(24,minmax(0,1fr))]"
            >
              <div class="key col-span-12">
                {{ $t("field.end-date-time") }}
              </div>
              <div class="value col-span-12">
                {{ formatDateTime(ts?.endDateTime) }}
              </div>
            </div>
          </div>
        </div>

        <SSeparator class="price-list-divider" />

        <div v-if="pc">
          <div class="price-select-wrapper" data-cy="prices-list-wrapper">
            <div
              class="price-selector flex items-center"
              v-for="(price, index) in sortedPrices(pc.prices)"
              :key="price.id"
              ref="pricesRef"
              data-cy="price-item"
            >
              <div class="grid w-full grid-cols-[repeat(24,minmax(0,1fr))]">
                <div class="price-name-wrapper col-span-6">
                  <p class="price-name">
                    {{ priceName(price) }}
                  </p>
                  <p class="price-description">
                    {{
                      truncatePriceDescription(
                        priceDescription(price),
                        priceDescriptionMaxLength
                      )
                    }}
                  </p>
                  <span
                    class="see-more"
                    v-if="
                      priceDescription(price).length > priceDescriptionMaxLength
                    "
                    @click="
                      togglePriceDescription(
                        index,
                        priceDescription(price),
                        priceDescriptionMaxLength
                      )
                    "
                    >{{ $t("button.see-more") }}</span
                  >
                </div>
                <div class="col-span-2 flex items-center justify-center">
                  <div class="price-accounting">{{ pc.product.currency }}</div>
                </div>
                <div class="col-span-3 flex items-center justify-center">
                  <div
                    class="price-value-container flex w-full items-center justify-center"
                    v-if="isPriceValueEditable(price)"
                  >
                    <div class="price-accounting editable">
                      {{
                        (
                          (getEditedPriceValue(price.id) || price.value) / 100
                        ).toFixed(2)
                      }}
                    </div>
                    <div class="edit-price-button">
                      <svg
                        @click="editPrice(price.id)"
                        data-cy="edit-price-button"
                        class="icon"
                        width="30"
                        height="30"
                        viewBox="0 0 1024 1024"
                        xmlns="http://www.w3.org/2000/svg"
                        data-v-042ca774=""
                      >
                        <path
                          fill="currentColor"
                          d="M832 512a32 32 0 1164 0v352a32 32 0 01-32 32H160a32 32 0 01-32-32V160a32 32 0 0132-32h352a32 32 0 010 64H192v640h640V512z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M469.952 554.24l52.8-7.552L847.104 222.4a32 32 0 10-45.248-45.248L477.44 501.44l-7.552 52.8zm422.4-422.4a96 96 0 010 135.808l-331.84 331.84a32 32 0 01-18.112 9.088L436.8 623.68a32 32 0 01-36.224-36.224l15.104-105.6a32 32 0 019.024-18.112l331.904-331.84a96 96 0 01135.744 0z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <div class="price-accounting" v-else>
                    <p>
                      {{
                        (
                          (pssCustomizedPriceValue(price.id) || price.value) /
                          100
                        ).toFixed(2)
                      }}
                    </p>

                    <p
                      v-if="isPssCustomizedPriceValue(price.id)"
                      style="font-size: 8px; line-height: 8px"
                    >
                      {{ $t("field.customized-price") }}
                    </p>
                  </div>
                </div>
                <div class="col-span-1 flex items-center justify-center"></div>
                <div
                  v-if="
                    !isRetail ||
                    price.retail[0].type ===
                      RetailOptionType.Default_Retail_Option
                  "
                  class="col-span-2 flex items-center justify-center"
                ></div>
                <div
                  class="col-span-8 flex flex-col items-center justify-center"
                >
                  <div class="flex items-center justify-center">
                    <SButton
                      type="button"
                      size="lg"
                      class="w-full price-button"
                      :class="{
                        transparent:
                          pricesCount[price.id] === 0 ||
                          countPssAllConfirmed(price.id, pricesCount[price.id]),
                      }"
                      :disabled="
                        isPssCustomizedPriceValue(price.id) ||
                        countPssAllConfirmed(price.id, pricesCount[price.id])
                      "
                      @click="decrease(price.id)"
                    >
                      -
                    </SButton>
                    <input
                      style="position: relative"
                      type="number"
                      class="price-count flex items-center justify-center"
                      v-model.number="pricesCount[price.id]"
                      :disabled="isPssCustomizedPriceValue(price.id)"
                      :min="countConfirmedPSS(price.id)"
                      data-cy="price-quantity-input"
                    />
                    <div>
                      <SButton
                        type="button"
                        size="lg"
                        class="w-full price-button"
                        :class="{
                          transparent: isPriceMaxQuantityReached(price.id),
                        }"
                        :disabled="isPssCustomizedPriceValue(price.id)"
                        @click="increase(price.id)"
                      >
                        +
                      </SButton>
                      <p
                        class="max-quantity-reached"
                        v-if="isPriceMaxQuantityReached(price.id)"
                      >
                        {{ $t("pcselect.max-quantity-reached") }}
                      </p>
                    </div>
                  </div>
                  <label
                    class="input-error"
                    v-if="
                      pricesCount[price.id] &&
                      !sellOnSoldOut(pc?.prices, pc?.type) &&
                      !sharedStocksWithOverbooking &&
                      pricesCount[price.id] > maxInputValue(price.id)
                    "
                    >{{
                      $t("error.quantiy-less-than") +
                      " " +
                      String(maxInputValue(price.id) + 1)
                    }}</label
                  >
                  <label
                    class="input-error"
                    v-if="pricesCount[price.id] < countConfirmedPSS(price.id)"
                    >{{
                      $t("error.quantiy-more-than") +
                      " " +
                      String(countConfirmedPSS(price.id) - 1)
                    }}</label
                  >
                </div>
                <div
                  v-if="
                    isRetail &&
                    price.retail[0].type !==
                      RetailOptionType.Default_Retail_Option
                  "
                  class="col-span-4 flex items-center justify-center"
                >
                  <div class="value">
                    {{ price.quantity - pricesCount[price.id] }}
                    {{ $t("pcselect.left") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SDialogFooter class="gap-3">
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ $t("button.cancel") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            :disabled="isDisabled || !canBook"
            data-cy="confirm-button"
            @click="addToBasket"
          >
            {{ $t("button.confirm") }}
          </SButton>
        </SDialogFooter>

        <!-- </div> -->
      </SDialogContent>
    </SDialog>
  </div>
  <calculator
    v-if="showNewPriceCalculator"
    @confirmNumber="handleCalculatorClick"
    @cancelCalculator="hideCalculator"
    :visible="showNewPriceCalculator"
    :placeHolderValue="pricePlaceHolder"
  ></calculator>
</template>

<script lang="ts">
import { useI18n } from "vue-i18n";
import {
  ModalComponents,
  ModalProps,
  ModalStatus,
  PCSelectModalProps,
} from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import {
  IProductCategory,
  IProductSlotPrice,
  ISmeetzPicture,
  PCTypes,
  RetailOptionType,
} from "@/types/db/product-category";
import { ITimeSlot } from "@/types/db/timeslots";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { computed, ref, defineComponent, PropType, onMounted } from "vue";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import shoppingIcon from "@/assets/images/icons/shopping-icon.png";
import { priceCountInBooking } from "@/utils/helpers/product-slot-booking";
import ModalModule from "@/store/modules/modal";
import { createPSS } from "@/utils/helpers/product-slot-state";
import { Currencies } from "@/constants/currency";
import {
  formatAndConvertToLocale,
  formatDateTime,
  nowDB,
} from "@/utils/helpers/dates";
import { getLanguage } from "@/utils/helpers/lang";
import { updateDiscount } from "@/utils/vue-helpers/discount";
import {
  BookingStatus,
  ICreateAddons,
  PaymentStatus,
  PSSType,
} from "@/types/db/product-slot-state";
import { createImageproxyUrl } from "@/utils/helpers/pictures";
import Calculator from "./calculator/Calculator.vue";
import {
  PCSELECT_IMAGE_HEIGHT,
  PCSELECT_IMAGE_MEASUREMENT_UNIT,
  PCSELECT_IMAGE_WIDTH,
} from "@/constants/style";
import {
  converttoICardData,
  filterPrices,
  getSameStockPCs,
  sellOnSoldOut,
} from "@/utils/helpers/product-category";
import { getProductCategoryById } from "@/dexie/product-category";
import { getTimeslotById } from "@/dexie/timeslots";
import { getPcStockUnits } from "@/utils/helpers/stock";
import {
  countPssByPricesAndTimeslots,
  getPriceAutomaticAddons,
  getPriceNonAutomaticAddons,
  getPssAvailability,
  getAutomaticAddonQuantityInBooking,
} from "@/utils/vue-helpers/product-slot-state";
import dayjs from "dayjs";
import NotificationService from "@/services/notification.service";
import { attachCustomFields } from "@/utils/vue-helpers/booking";
import { LogLevel, LogService } from "@/services/logging";
import { cloneDeep } from "lodash";
import PSSModule from "@/store/modules/pss";
import {
  createDiscountCodeAccounting,
  createPaymentCodeAccounting,
  hasAppliedGenericDiscountTracking,
  removeAllDiscountCodePendingAccountings,
  removeAllPaymentCodePendingAccountings,
  updateGenericDiscountAccountings,
} from "@/utils/vue-helpers/new-accountings";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  AccountingState,
  PaymentMethods,
  PromocodeCampaignType,
} from "@/types/db/accounting";
import POSConfigModule from "@/store/modules/pos-config";
import { hasAddonsEnabled } from "@/utils/vue-helpers/addons";
import { recordOperationDuration } from "@/utils/vue-helpers/common";
import { PSSErrors } from "@/constants/errors/product-slot-state";
import { recreateIncertVoucherAccountings } from "@/api/external-vouchers/incert";
import { applyOffersToBooking } from "@/utils/helpers/offers";
import { processAdditionalEvents } from "@/utils/helpers/compliance";
import { EventType } from "@smeetz/compliance-common";
import { syncBookingAvailabilities } from "@/utils/helpers/booking";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SSeparator,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.PCSelect,
  components: {
    Calculator,
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SSeparator,
  },
  props: {
    modalProps: {
      type: Object as PropType<ModalProps>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n({});
    // Modal is visible for PCSelect
    const dialogVisible = dialogVisibleForStatus(ModalStatus.PCSelect);
    const pc = ref<IProductCategory | null>(null);
    const ts = ref<ITimeSlot | null>(null);
    const prices = ref<Record<number, IProductSlotPrice>>({});
    const pricesCount = ref<Record<number, number>>({});
    const pricesBookingCount = ref<Record<number, number>>({});
    const isLoading = ref<boolean>(false);
    const showSuccessMessage = ref<boolean>(false);

    const currentLanguage = ref(getLanguage());
    const editingPrice = ref<number>();
    const editedPrices = ref<
      { priceId: number; oldPrice: number; newPrice: number | null }[]
    >([]);
    const showNewPriceCalculator = ref<boolean>(false);
    const pricePlaceHolder = ref<number>(0);
    const priceDescriptionMaxLength = 80;

    const isRetail = computed(() => {
      return pc.value?.type === PCTypes[PCTypes.Retail];
    });

    const sharedStocksWithOverbooking = computed(() => {
      return (
        pc.value?.stockIds &&
        pc.value?.stockIds.length &&
        pc.value.stockIds.includes(-1000)
      );
    });

    // Count pending items from other shared-stock products already in the cart
    const sharedStockPendingCount = computed(() => {
      if (isRetail.value || !pc.value || !ts.value) return 0;
      const totalPending = countPssByPricesAndTimeslots(
        pc.value.prices,
        pc.value.id,
        {
          timeslotId: String(ts.value.id),
          startDateTime: String(ts.value.startDateTime),
          endDateTime: String(ts.value.endDateTime),
        },
        getPcStockUnits(pc.value.stockConsumptions)
      );
      // totalPending includes items from this product too, which are already
      // tracked by pricesBookingCount/userInputSum, so subtract them
      const thisProductCount = Object.values(pricesBookingCount.value).reduce(
        (a, b) => a + b,
        0
      );
      return Math.max(0, totalPending - thisProductCount);
    });

    const isDisabled = computed(() => {
      if (pc.value && pc.value?.prices && pc.value?.prices.length) {
        for (let i = 0; i < pc.value.prices.length; i++) {
          const priceId = pc.value.prices[i].id;
          if (pricesCount.value[priceId] < countConfirmedPSS(priceId)) {
            return true;
          }
          if (
            !sellOnSoldOut(pc.value?.prices, pc.value?.type) &&
            !sharedStocksWithOverbooking.value &&
            pricesCount.value[priceId] > maxInputValue(priceId)
          ) {
            return true;
          }
        }
        return false;
      } else {
        return true;
      }
    });

    onMounted(async () => {
      // process price lookup event
      processAdditionalEvents(EventType.PRICE_LOOKUP).catch((error) => {
        LogService.info("Error processing PRICE LOOKUP event", {
          error,
        });
      });

      await getPC();
    });

    const availabilities = computed(() => {
      // If retail product then sum all prices.quantity of the product category
      // If ticket product then return timeslot.availability
      let avail = 0;
      if (isRetail.value) {
        if (
          pc.value?.prices.length === 1 &&
          pc.value?.prices[0].retail[0].type ===
            RetailOptionType.Default_Retail_Option
        ) {
          // It is retail product but with only one default retail option
          avail = pc.value?.prices[0].quantity;
        } else {
          // this retail has options other than the default one
          pc.value?.prices
            .filter((p) =>
              p.retail.every(
                (r) => r.type !== RetailOptionType.Default_Retail_Option
              )
            )
            .map((p) => p.quantity)
            .forEach((quantity) => (avail = avail + quantity));
        }
      } else {
        avail = (ts.value?.availability || 0) - sharedStockPendingCount.value;
      }

      return avail - userInputSum.value;
    });

    const userInputSum = computed(() => {
      let count = 0;
      if (!pricesCount.value) {
        return 0;
      }

      for (const priceId of Object.keys(pricesCount.value)) {
        const numPriceId = Number(priceId);
        count = count + pricesCount.value[numPriceId];
      }

      return count;
    });

    async function getPC() {
      const ctx = props.modalProps.props as PCSelectModalProps | undefined;
      const pcId = Number(ctx?.productCategoryId);
      let selectedPC: IProductCategory | undefined;
      try {
        if (!pcId) {
          throw new Error("PCSelect opened without a product category id");
        }
        selectedPC = await getProductCategoryById(pcId);
      } catch (error) {
        NotificationService.showGeneralResponseError();
        LogService.info("Error fetching selected PC", { error });
      }
      if (!selectedPC) {
        return;
      }
      pc.value = filterPrices(selectedPC);

      if (!isRetail.value) {
        ts.value = ctx?.timeslotId
          ? (await getTimeslotById(Number(ctx.timeslotId))) || null
          : null;
        if (!ts.value) {
          LogService.info(
            `Quantity dialog opened without a resolvable timeslot`,
            {
              groupId: POSConfigModule.groupId,
              productCategoryId: pcId,
              timeslotId: ctx?.timeslotId || null,
            },
            LogLevel.Error
          );
        }
      }

      const booking = BookingModule.selectedBooking;

      if (!booking) {
        throw new Error(PSBErrors.NoBookingInModule);
      }

      const pricesCountt = pricesCount.value;
      const pricesBookingCountt = pricesBookingCount.value;
      const pcPricess = pc.value?.prices || [];
      const pricess = prices.value;

      // build price related objects object (count, diff & count in booking)
      for (const price of pcPricess) {
        const priceId = price.id;
        const priceCount = priceCountInBooking(booking, {
          priceId: priceId,
          timeslotId: ts.value?.id,
          categoryId: pc.value?.id,
        });

        // Initially, count in booking and component is the same
        pricesBookingCountt[priceId] = priceCount;
        pricesCountt[priceId] = priceCount;
        pricess[priceId] = price;
      }
    }

    /**
     * If merch product then return pc.name
     * If ticket product then return pc.product.name + pc.name
     */
    const productCategoryFullName = computed(() => {
      return isRetail.value
        ? pc.value?.nameLocale[currentLanguage.value] || pc.value?.name
        : (pc.value?.product.nameLocale[currentLanguage.value] ||
            pc.value?.product.name ||
            "") +
            " - " +
            (pc.value?.nameLocale[currentLanguage.value] || pc.value?.name);
    });

    /**
     * If merch product then return retailOption name
     * If ticket product then return priceName
     */
    function priceName(price: IProductSlotPrice) {
      // if it is a ticket product then we return the translated name of the price if there is translation, otherwise we return the price name
      // if it is a retail product then we return the translated name of the retail if there is translation, otherwise we return the price name
      // if it is a retail product but there are not retail options, there is only the default retail, then we consider the categoryName name and its translations
      if (!isRetail.value) {
        return price.nameLocale
          ? price.nameLocale[currentLanguage.value] || price.name
          : price.name;
      }

      if (isRetail.value) {
        if (price.retail[0].type !== RetailOptionType.Default_Retail_Option) {
          return price.retail[0].nameLocale
            ? price.retail[0].nameLocale[currentLanguage.value] ||
                price.retail[0].name
            : price.retail[0].name;
        }
        if (price.retail[0].type === RetailOptionType.Default_Retail_Option) {
          return pc.value?.nameLocale[currentLanguage.value] || pc.value?.name;
        }
      }
    }

    /**
     * If merch product then return retailOption description (not available in db yet)
     * If ticket product then return priceDescription
     */
    function priceDescription(price: IProductSlotPrice): string {
      return isRetail.value
        ? ""
        : price.descriptionLocale
        ? price.descriptionLocale[currentLanguage.value] || price.description
        : price.description;
    }

    let productPicture = computed(() => {
      if (pc.value) {
        if (!isRetail.value) {
          return pc.value?.product.pictureUrl;
        } else {
          return (
            createImageproxyUrl(getDefaultRetailOptionSmeetzPicture(pc.value), {
              width: 150 /*Number(PCSELECT_IMAGE_WIDTH)*/,
              height: 150 /*Number(PCSELECT_IMAGE_HEIGHT)*/,
            }) || shoppingIcon
          );
        }
      } else {
        return shoppingIcon;
      }
    });

    const pricesDiff = computed(() => {
      let diffs: Record<number, number> = {};
      if (!pricesCount.value) {
        return {};
      }

      for (const priceId of Object.keys(pricesCount.value)) {
        const numPriceId = Number(priceId);
        diffs[numPriceId] =
          pricesCount.value[numPriceId] - pricesBookingCount.value[numPriceId];
      }

      return diffs;
    });

    // Checks whether user can book or not
    const canBook = computed(() => {
      // if we have non zero diff on a price, we can book
      for (const priceId of Object.keys(pricesDiff.value)) {
        const priceDiff = pricesDiff.value[Number(priceId)];
        if (pricesDiff.value[Number(priceId)] !== 0) {
          return true;
        }
      }
      return false;
    });

    function isPriceMaxQuantityReached(priceId: number) {
      // Will be used to make sure that we have enough availability for the price the user is trying to increase from
      // If retail product then price.quantity of the current price is the availability
      // If ticket product then we consider timeslot.availability
      let avail = 0;
      if (isRetail.value) {
        avail = pc.value?.prices.find((p) => p.id === priceId)?.quantity || 0;
      } else {
        avail = (ts.value?.availability || 0) - sharedStockPendingCount.value;
      }
      const priceCount = pricesCount.value[priceId];
      if (
        priceCount >= avail &&
        !sellOnSoldOut(pc.value?.prices, pc.value?.type) &&
        !sharedStocksWithOverbooking.value
      ) {
        return true;
      }
      return false;
    }

    const getModalTop = computed(() => {
      // Returns how close the modal is to the top of the window
      const defaultTop = 15;
      const toBeDeductedTop = !pc.value ? 0 : pc.value?.prices.length * 3.5;
      const top =
        defaultTop > toBeDeductedTop
          ? defaultTop - toBeDeductedTop + "vh"
          : "0vh";
      return top;
    });

    /**
     * Takes the Product Category and returns RetailOption.SmeetzPictures of the first price for picture display use
     * If the retail has the only one option we show the default option picture
     * Otherwise we show the first retaoil option picture
     * Why we don't always show the default retail option picture?
     * Because when the retail has many options we are not considering the default one inside pc.prices (it will not exist)
     */
    function getDefaultRetailOptionSmeetzPicture(
      pc: IProductCategory
    ): ISmeetzPicture | undefined {
      const smeetzPicture = pc.prices[0].retail[0].smeetzPictures[0];
      return smeetzPicture;
    }

    /**
     * Price value is editable if price.isEditable===1 and there is no pss already created to simplify things in the begining
     */
    function isPriceValueEditable(price: IProductSlotPrice) {
      if (!price.isEditable) return false;
      const pssList = BookingModule.selectedBooking?.pssList.filter(
        (pss) => pss.priceId === price.id
      );

      return pssList && pssList.length ? false : true;
    }
    /**
     * Considering a priceId, this function returns whether or not the existing PSS of the booking  from this price has a customized price value or the original price valie
     */
    function isPssCustomizedPriceValue(priceId: number) {
      const pssList = BookingModule.selectedBooking?.pssList.filter(
        (pss) => pss.priceId === priceId
      );
      if (!pssList) {
        return false;
      }

      const originalPriceValue = pc.value?.prices.find(
        (pr) => pr.id === priceId
      )?.value;
      const isCustomized = pssList?.some((pss) => {
        // lets ignore the addons
        if (pss.mainTicketUUID) return false;
        // If payment has started then pss.discountValue has been already deducted from pss.value,
        // lets consider this case too since user is able to come back here when the payment is processing
        if (pss.paymentStatus === PaymentStatus.Started && pss._discountValue)
          return pss.priceValue + pss._discountValue !== originalPriceValue;
        else {
          return pss.priceValue !== originalPriceValue;
        }
      });
      return isCustomized;
    }

    /**
     * Considering a priceId and count, this function checks whether all corresponding pss are confirmed or not
     * We need it to disable user from decreasing quantity when this quantity is actually a list of confirmed pss,
     * because confirmed pss should only be removed explicitly by the user when clicking on remove-button on the basket so that cancellation accountings will be created
     */
    function countPssAllConfirmed(priceId: number, count: number) {
      const confirmedPssList = BookingModule.selectedBooking?.pssList.filter(
        (pss) =>
          pss.priceId === priceId &&
          pss.bookingStatus === BookingStatus.Confirmed &&
          (pss.pssType === PSSType[PSSType.Retail] ||
            pss.timeSlotId === ts.value?.id)
      );
      if (!confirmedPssList) {
        return false;
      }

      if (confirmedPssList.length === count) {
        return true;
      }
      return false;
    }

    function countConfirmedPSS(priceId: number): number {
      const confirmedPssList = BookingModule.selectedBooking?.pssList.filter(
        (pss) =>
          pss.priceId === priceId &&
          pss.bookingStatus === BookingStatus.Confirmed &&
          (pss.pssType === PSSType[PSSType.Retail] ||
            pss.timeSlotId === ts.value?.id)
      );
      if (confirmedPssList && confirmedPssList.length) {
        return confirmedPssList.length;
      }
      return 0;
    }

    function maxInputValue(priceId: number): number {
      return Math.max(availabilities.value + pricesCount.value[priceId], 0);
    }

    function pssCustomizedPriceValue(priceId: number): number | null {
      if (!isPssCustomizedPriceValue(priceId)) return null;
      const pssList = BookingModule.selectedBooking?.pssList.filter(
        (pss) => pss.priceId === priceId
      );

      if (!pssList || !pssList.length) {
        return null;
      }

      const pss = pssList[0];

      if (pss.paymentStatus === PaymentStatus.Started && pss._discountValue)
        return pss.priceValue + pss._discountValue;
      else {
        return pss.priceValue;
      }
    }

    /**
     * Returns the new value of the price that the user has entered manually, not original price value
     */
    function getEditedPriceValue(priceId: number): number | null {
      const editedPrice = this.editedPrices.find(
        (p: any) => p.priceId === priceId
      );
      if (!editedPrice) {
        return null;
      }
      return editedPrice.newPriceValue;
    }

    const pcselectPictureSize = computed(() => {
      return {
        width: PCSELECT_IMAGE_WIDTH + PCSELECT_IMAGE_MEASUREMENT_UNIT,
        height: PCSELECT_IMAGE_HEIGHT + PCSELECT_IMAGE_MEASUREMENT_UNIT,
      };
    });

    function sortedPrices(prices: IProductSlotPrice[]): IProductSlotPrice[] {
      console.log("----- hi from sortedPrices");
      console.log("prices");
      console.log(prices);
      // for ticket products, sort by priceSort
      if (!isRetail.value) {
        return prices.sort((a, b) => (a.sortPrice ?? 25) - (b.sortPrice ?? 25));
      }
      // for retail products, sort by retail.optionSort
      return prices.sort(
        (a, b) =>
          (a.retail[0].optionSort ?? 25) - (b.retail[0].optionSort ?? 25)
      );
    }

    function togglePriceDescription(
      index: number,
      description: string,
      max: number
    ) {
      const row = this.$refs.pricesRef[index];
      const currentDescription = row.querySelector(".price-description");
      const seeMoreLess = row.querySelector(".see-more");
      if (currentDescription.innerText.length < description.length) {
        currentDescription.innerText = description;
        seeMoreLess.innerText = t("button.see-less");
      } else {
        currentDescription.innerText = truncatePriceDescription(
          description,
          max
        );
        seeMoreLess.innerText = t("button.see-more");
      }
    }

    function truncatePriceDescription(text: string, max: number): string {
      return text.length > max ? text.slice(0, max) + " ..." : text;
    }

    return {
      RetailOptionType,
      t,
      dialogVisible,
      pc,
      ts,
      pricesCount,
      pricesDiff,
      canBook,
      isLoading,
      prices,
      currentLanguage,
      isRetail,
      availabilities,
      productCategoryFullName,
      productPicture,
      isPriceMaxQuantityReached,
      priceName,
      priceDescription,
      formatAndConvertToLocale,
      getModalTop,
      editingPrice,
      editedPrices,
      showNewPriceCalculator,
      isPriceValueEditable,
      pricePlaceHolder,
      getEditedPriceValue,
      isPssCustomizedPriceValue,
      countPssAllConfirmed,
      pssCustomizedPriceValue,
      pcselectPictureSize,
      formatDateTime,
      sortedPrices,
      countConfirmedPSS,
      isDisabled,
      maxInputValue,
      sellOnSoldOut,
      sharedStocksWithOverbooking,
      togglePriceDescription,
      truncatePriceDescription,
      priceDescriptionMaxLength,
      hideDialog,
      showSuccessMessage,
    };
  },
  methods: {
    increase(priceId: number) {
      if (this.isPriceMaxQuantityReached(priceId)) {
        // At this point we wannot increase, so lets just leave
        // TODO: Do something to inform user that this is the max quantity available of the price he is trying increase from
        return;
      }
      const priceCount = this.pricesCount[priceId];
      this.pricesCount[priceId] = priceCount + 1;
    },
    decrease(priceId: number) {
      const priceCount = this.pricesCount[priceId];

      if (priceCount === 0) {
        return;
      }
      this.pricesCount[priceId] = priceCount - 1;
    },
    async addToBasket() {
      const operationStartTime = performance.now();

      const pricesDiff = this.pricesDiff;
      const booking = BookingModule.selectedBooking;

      if (!booking) {
        throw new Error(PSBErrors.NoBookingInModule);
      }
      const currentPSSCount = booking.pssList.length;

      // pc and ts were already checked so cast them directly
      const pc = this.pc as IProductCategory;
      const ts = this.ts as ITimeSlot;
      if (
        !this.isRetail &&
        ts &&
        pc &&
        pc.type === PCTypes[PCTypes.Normal] &&
        ts.categoryId !== pc.id
      ) {
        LogService.info(
          `Timeslot category mismatch in the quantity dialog, blocking the add`,
          {
            groupId: POSConfigModule.groupId,
            pssCategoryId: pc.id,
            tsCategoryId: ts.categoryId,
            tsId: ts.id,
          },
          LogLevel.Error
        );
        NotificationService.showCustomError(
          this.t("error.ticket-slot-mismatch")
        );
        return;
      }
      const bookingUUID = booking.uuid as string;
      const prices = this.prices as Record<number, IProductSlotPrice>;
      const { id: productCategoryId } = pc;
      this.isLoading = true;
      const pricesList = Object.keys(pricesDiff).map((priceId) =>
        Number(priceId)
      );
      // check if prices require addons
      let goToAddonsStep = false;
      const pricesToAddToBasket: {
        price: IProductSlotPrice;
        pssQuantity: number;
      }[] = [];
      for (let i = 0; i < pricesList.length; i++) {
        const priceId = pricesList[i];
        const price = prices[priceId];
        if (pricesDiff[priceId] > 0) {
          pricesToAddToBasket.push({
            price,
            pssQuantity: pricesDiff[priceId],
          });
          const addons = await getPriceNonAutomaticAddons(price);
          if (addons.length) {
            goToAddonsStep = true;
          }
        }
      }
      // redirect to addons popup if prices require addons
      if (goToAddonsStep && hasAddonsEnabled.value) {
        // we convert the selected pc into a ICardData object so we can pass it as a prop to the addons modal
        const productCardData = converttoICardData([pc])[0];
        ModalModule.setModalProps({
          props: {
            selectedProduct: productCardData,
            productCategory: pc,
            isRetail: this.isRetail,
            pricesToAddToBasket: pricesToAddToBasket,
            timeslot: ts || undefined,
          },
        });
        ModalModule.setStatus(ModalStatus.Hidden);
        await this.$nextTick();
        ModalModule.setStatus(ModalStatus.Addons);
        return;
      }
      // add and remove from booking all prices that have a non zero diff
      for (const priceId of pricesList) {
        const priceDiff = pricesDiff[priceId];

        // skip price if it has no diff
        if (priceDiff === 0) {
          continue;
        }

        // In case we added prices
        if (priceDiff > 0) {
          // Create pss
          const overridenPrice = this.getEditedPriceValue(priceId)
            ? {
                originalPrice: prices[priceId].value,
                newPrice: this.getEditedPriceValue(priceId),
              }
            : undefined;
          const priceValue = this.getEditedPriceValue(priceId)
            ? this.getEditedPriceValue(priceId)
            : prices[priceId].value;
          // let get the addons related to this product price
          let pssAddons: ICreateAddons[] = [];
          try {
            pssAddons = await getPriceAutomaticAddons(
              prices[priceId],
              booking,
              ts ? ts.startDateTime : undefined,
              ts ? String(ts?.id) : undefined
            );
          } catch (error) {
            if (error?.message === PSSErrors.UnavailableMandatoryAddon) {
              // a custom error is thrown in case of unavailable mandatory addons
              this.isLoading = false;
              const unavailableAddon = error.addon;
              const msg = this.t("error.unavailable-addon").replace(
                "XXX",
                unavailableAddon
              );
              NotificationService.showCustomError(msg);
              return;
            }
          }

          // check the availability of the addons
          // Only a MANDATORY unavailable addon blocks the sale;
          // an unavailable optional addon is skipped with a warning.
          let canBook = true;
          const availableAddons: ICreateAddons[] = [];
          for (const addon of pssAddons) {
            const availability = getPssAvailability(addon);
            const isUnavailable =
              availability < priceDiff &&
              !sellOnSoldOut(
                addon.productCategory.prices,
                addon.productCategory.type
              ) &&
              !this.sharedStocksWithOverbooking;
            if (!isUnavailable) {
              availableAddons.push(addon);
              continue;
            }
            if (addon.isMandatory) {
              canBook = false;
              const msg = this.t("availability.autumatic-addon")
                .replace("X1", addon.productCategory.name)
                .replace("X2", String(availability));
              NotificationService.showCustomError(msg);
            } else {
              const msg = this.t("availability.optional-addon-skipped", {
                addonName: addon.productCategory.name,
              });
              NotificationService.showCustomMessage(msg, "warning");
            }
          }
          if (!canBook) {
            this.isLoading = false;
            return;
          }
          pssAddons = availableAddons;

          // if this pss have addons let's get the last endDateTime to be in the qrcode token
          let tokenEndDate: string | null = ts?.endDateTime || null;
          for (const addon of pssAddons) {
            if (!addon.timeslot) continue;
            if (
              !tokenEndDate ||
              (tokenEndDate &&
                dayjs(addon.timeslot.endDateTime).isAfter(tokenEndDate))
            ) {
              tokenEndDate = addon.timeslot.endDateTime;
            }
          }

          const basePSSConfig = {
            pssType: this.isRetail
              ? PSSType[PSSType.Retail]
              : PSSType[PSSType.Ticket],
            bookingUuid: bookingUUID,
            priceId,
            categoryId: productCategoryId,
            priceValue,
            timeslot: ts,
            productCategory: pc,
            sellerId: booking.sellerId as number, // create booking will throw if we have no seller id
            sendTicket: 1,
            token: null, // it will be set later
            mainSlot: null,
            packagePriceId: null,
            tokenEndDate,
            bookingReference: booking.reference,
            overridenPrice,
          };

          let pssList = await createPSS({
            ...basePSSConfig,
            count: 1,
            addons: pssAddons,
          });
          if (pssList && pssList.length) {
            this.showSuccessMessage = true;
          }

          if (priceDiff > 1) {
            // if the quantity to add is larger than 1 and max addon quantity constraint was applied,
            // then the rest of the addons are added only if their max quantity is still greater than than their quantity in the booking
            const restOfAddons: ICreateAddons[] = [];
            for (let i = 0; i < pssAddons.length; i++) {
              const addon = pssAddons[i];
              if (addon.accountingValue) {
                // chillon package addons are excluded from this logic and have to be added either way
                restOfAddons.push(addon);
                continue;
              }
              const addonQtyInBooking = getAutomaticAddonQuantityInBooking(
                addon.packagePriceId as number,
                booking
              );
              // max quantity of addon has to be greater than ((addon quantity in booking) + (addon quantity that will be created) + 1 (to account for the addon already added above))
              if (
                (addon.maxQuantity || 0) >
                addonQtyInBooking +
                  restOfAddons.filter(
                    (a) => a.packagePriceId === addon.packagePriceId
                  ).length +
                  1
              ) {
                restOfAddons.push(addon);
              }
            }
            const restOfPSS = await createPSS({
              ...basePSSConfig,
              count: priceDiff - 1,
              addons: restOfAddons,
            });
            pssList = [...pssList, ...restOfPSS];
          }

          // add them to current booking
          BookingModule.addPSS({
            bookingUUID,
            pssList,
          });

          // For each pss created lets add its extension.
          // Each pss must reflect availability only onto the PCs that share ITS
          // OWN stock entity. A combined same-stock set for the whole pssList
          // would make a main ticket claim the addon's stock PCs (and vice
          // versa), double-counting availability for overlapping timeslots
          // (L3-7768).
          const sameStockPCsByCategory = new Map<number, number[]>();
          for (const pss of pssList) {
            if (!sameStockPCsByCategory.has(pss.categoryId)) {
              const pcs = await getSameStockPCs([String(pss.categoryId)]);
              sameStockPCsByCategory.set(
                pss.categoryId,
                pcs && pcs.length ? pcs.map((p) => p.id) : []
              );
            }
          }
          for (const pss of pssList) {
            let psc = pc;
            if (pss.categoryId !== pc.id) {
              // Add-on pss (automatic or manually selected) belong to their
              // own product category. The extension must be built from that
              // PC, since its ticketGroups drive promo/sale-collection
              // eligibility and its prices carry the taxes of the add-on line.
              const addonPC = await getProductCategoryById(pss.categoryId);
              if (addonPC) {
                psc = addonPC;
              }
            }
            const sameStockPCsIds =
              sameStockPCsByCategory.get(pss.categoryId) || [];
            if (sameStockPCsIds.length) {
              PSSModule.addPSSExtensions([
                { psc, pss, sameStockPCs: sameStockPCsIds },
              ]);
            } else {
              PSSModule.addPSSExtensions([{ psc, pss }]);
            }
          }

          // If this the first PC of this booking then update the booking currency
          if (currentPSSCount === 0) {
            // So the booking has the default currency (CHF), lets update it
            // (creating a new booking object to avoid updating the original object in place)
            const updatedBooking = Object.assign(
              {},
              BookingModule.selectedBooking /*booking*/
            );
            // Cast the currency string into a property of object type Currencies (USD, EUR, CHF..), then use it as an index Currencies[key]
            const key = pc.product.currency as keyof typeof Currencies;
            updatedBooking.currency = Currencies[key];
            BookingModule.updateBooking(updatedBooking);
          }

          // Add custom fields if necessary
          if (
            (pc.product.customBookingFields &&
              pc.product.customBookingFields.length) ||
            (pc.product.customTicketFields &&
              pc.product.customTicketFields.length)
          ) {
            LogService.info(
              `So PC has custom fields, attaching custom fields to the booking and/or pss for booking ${bookingUUID}`
            );
            attachCustomFields({
              pc,
              pssList: pssList.map((pss) => {
                return {
                  priceId: pss.priceId,
                  pssUuid: String(pss.uuid),
                  priceName: pss.priceName,
                  categoryId: pss.categoryId,
                  categoryName: pss.categoryName,
                };
              }),
            });
          }

          // THIS IS NO LONGER NEEDED SINCE WE ARE CREATING PAYMENT CODES SOMEWHERE ELSE
          // // Now if the new PSS created are included in a Payment Promocode (voucher/giftcard)
          // // then we need to add them to the Code Payment accounting and create their TICKET accounting
          // // and confirm their status
          // const pssOfThePaymentCode = BookingModule.promocodeDiscountValues
          //   .filter(
          //     (p) =>
          //       p.codeTransactionType === CodeTransactionType.Payment &&
          //       p.promocodeDiscountValue > 0
          //   )
          //   .map((p) => p.pssUuid);
          // if (pssOfThePaymentCode) {
          //   // So we have a payment code applied
          //   for (const pss of pssList) {
          //     if (pssOfThePaymentCode.includes(String(pss.uuid))) {
          //       // So this newly created pss is considered in the applied payment Code
          //       // lets check if we already have an accounting for the applied code
          //       // so that we create accountingEntry for the pss and attach it to this accounting

          //       // lets get applied code id
          //       const appliedPaymentCodeId =
          //         BookingModule.appliedPromocode?.promocode.promoCodeId;
          //       // lets get payment code accounting
          //       const paymentCodeAccounting =
          //         BookingModule.selectedBooking?.accountings.find(
          //           (ac) => ac.promoCodeId === appliedPaymentCodeId
          //         );
          //       if (paymentCodeAccounting) {
          //         // 1- lets create accountingEntry for the pss
          //         createPromocodePaymentAccountingEntries(
          //           paymentCodeAccounting,
          //           [pss]
          //         );
          //         // 2- lets add TICKET accounting for the current pss
          //         const bookingCopy = cloneDeep(BookingModule.selectedBooking);
          //         if (bookingCopy) {
          //           bookingCopy.accountings.push(
          //             createTicketAccounting(pss, bookingCopy.currency)
          //           );
          //           BookingModule.updateBooking(bookingCopy);
          //         }
          //       }
          //     }
          //   }
          // }

          // Now if the pss that had the code applied are now fully paid, we need to complete there status
          const bookingCopy = cloneDeep(BookingModule.selectedBooking);
          if (bookingCopy) {
            // If a pss is cancelled we don't complete it
            // If a pss has some amount paid we complete its bookingStatus
            // If a pss is fully paid we complete its paymentStatus
            const pssListBalances = BookingModule.pssListBalances;

            for (const pss of bookingCopy.pssList) {
              if (
                pss.bookingStatus === BookingStatus.Canceled ||
                pss.bookingStatus === BookingStatus.Abandoned
              ) {
                // cancelled/abandoned pss are not to be confirmed
                continue;
              }

              // if pss has a payment we confirmed we confirm its booking status
              const balance = pssListBalances.find(
                (b) => b.pssUuid === pss.uuid
              );
              if (!balance) {
                continue;
              } else {
                if (balance.accEntries && balance.accEntries.length) {
                  // so this pss has payment confirmed
                  pss.bookingStatus = BookingStatus.Confirmed;

                  // if pss is actually fully paid then we confirm its paymentStatus
                  if (balance.balance === 0) {
                    pss.paymentStatus = PaymentStatus.Completed;
                  }
                }
              }
              pss.updatedAt = nowDB();
            }
            BookingModule.updateBooking(bookingCopy);
          }

          // DiscountTracking update
          await updateDiscount(priceId, pssList);
        }

        // Case were we removed prices
        if (priceDiff < 0) {
          // remove PSS from booking
          BookingModule.removePSS({
            bookingUUID,
            criteria: {
              pssType: this.isRetail
                ? PSSType[PSSType.Retail]
                : PSSType[PSSType.Ticket],
              retailOptionId: this.isRetail
                ? prices[priceId].retail[0].id
                : undefined,
              timeslot: ts,
              productCategoryId: productCategoryId,
              priceId,
              bookingUuid: bookingUUID,
              count: priceDiff * -1, // Must be positive
            },
          });

          // apply offers after pss list is updated
          applyOffersToBooking(
            cloneDeep(BookingModule.selectedBooking as IProductSlotBooking)
          );

          // DiscountTracking update
          await updateDiscount(priceId);

          // if currently there is no pss in the booking we reset the currency to the default one
          if (!BookingModule.hasTickets) {
            // (creating a new booking object to avoid updating the original object in place)
            const updatedBooking = Object.assign({}, booking);
            const groupCurrencies = POSConfigModule.groupCurrencies;
            const groupCurrency =
              groupCurrencies.find((c) => c.isDefault === 1) ||
              groupCurrencies[0];
            const groupCurrencyLabel = groupCurrency.label;
            updatedBooking.currency = groupCurrencyLabel;
            BookingModule.updateBooking(updatedBooking);
          }
        }
      }

      // New Logic of creating genericDiscount/promocodeDiscounts/paymentCodes accounting
      // TODO Hamza: we always update created acountings of genericDiscount/promocodeDiscunt/paymentCode here
      // NOTE: this entire block duplicates `removeAndReapplyPromocodes` + `recreateIncertVoucherAccountings`.
      // It could be replaced by calling those two helpers (as done in deleteUnconfirmedTickets).
      // Kept inline for now to avoid behavioral risk on the add-to-basket path; deduplicate when refactoring.
      const hasGenericDiscount = hasAppliedGenericDiscountTracking.value;
      let hasPendingGenericDiscountAccounting = false;
      const genericDiscountAccIndex = (
        BookingModule.selectedBooking as IProductSlotBooking
      ).accountings.findIndex(
        (acc) =>
          acc.state === AccountingState.Pending &&
          !acc.refund &&
          acc.paymentMethod === PaymentMethods.METHODS_POS_DISCOUNT
      );
      if (genericDiscountAccIndex > -1) {
        hasPendingGenericDiscountAccounting = true;
      }
      const hasPromocodeDiscount = true;
      const hasPaymentCode = true;
      if (hasGenericDiscount || hasPendingGenericDiscountAccounting) {
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        /////////////////
        // TODO Hamza: If we have applied codes for this booking, then we remove their accountings
        //     - Remove pending payment code accountings of all applied payment codes
        removeAllPaymentCodePendingAccountings(bookingCopy);
        //     - Remove pending discount code accountings of all applied discount codes
        removeAllDiscountCodePendingAccountings(bookingCopy);
        // Now lets update the booking in the state before appyling the discount, and before applying any code
        // BookingModule.updateBooking(bookingCopy);
        /////////////////
        updateGenericDiscountAccountings(bookingCopy);
        BookingModule.updateBooking(bookingCopy);

        // Reusing bookingCopy for recreating codes accountings will not push restToPay to react
        const bookingCopy1 = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        /////////////////
        // TODO Hamza: Recreate accountings for applied discount codes and then payment codes
        //     - Create discount code accountings of all applied discount codes
        // lets get all applied discount codes and sort them by applyDateTime
        const sortedAppliedDiscountCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
          createDiscountCodeAccounting(
            bookingCopy1,
            sortedAppliedDiscountCodes[i]
          );
        }
        // BookingModule.updateBooking(bookingCopy);
        //     - Create payment code accountings of all applied payment codes
        // lets get all applied payment codes and sort them by applyDateTime
        const sortedAppliedPaymentCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
          createPaymentCodeAccounting(
            bookingCopy1,
            sortedAppliedPaymentCodes[i]
          );
        }
        BookingModule.updateBooking(bookingCopy1);
        /////////////////
      } else {
        // There is no generic discount applied
        // We remove any discount/payment codes accounting that were generated by an applied code
        // and then we recreate them
        const bookingCopy = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        //     - Remove pending payment code accountings of all applied payment codes
        removeAllPaymentCodePendingAccountings(bookingCopy);
        //     - Remove pending discount code accountings of all applied discount codes
        removeAllDiscountCodePendingAccountings(bookingCopy);
        BookingModule.updateBooking(bookingCopy);

        // Reusing bookingCopy for recreating codes accountings will not push restToPay to react
        const bookingCopy1 = cloneDeep(
          BookingModule.selectedBooking as IProductSlotBooking
        );
        /////////////////
        // TODO Hamza: Recreate accountings for applied discount codes and then payment codes
        //     - Create discount code accountings of all applied discount codes
        // lets get all applied discount codes and sort them by applyDateTime
        const sortedAppliedDiscountCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_VOUCHER_SOLD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD &&
              p.promocode.promoCodeType !==
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        for (let i = 0; i < sortedAppliedDiscountCodes.length; i++) {
          createDiscountCodeAccounting(
            bookingCopy1,
            sortedAppliedDiscountCodes[i]
          );
        }
        // BookingModule.updateBooking(bookingCopy);
        //     - Create payment code accountings of all applied payment codes
        // lets get all applied payment codes and sort them by applyDateTime
        const sortedAppliedPaymentCodes =
          BookingModule.appliedPromocodes.filter(
            (p) =>
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD ||
              p.promocode.promoCodeType ===
                PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
          ); /*.sort((p) => p.createdAt)*/
        for (let i = 0; i < sortedAppliedPaymentCodes.length; i++) {
          createPaymentCodeAccounting(
            bookingCopy1,
            sortedAppliedPaymentCodes[i]
          );
        }
        BookingModule.updateBooking(bookingCopy1);
      }

      // Now lets recreate Incert voucher accountings
      recreateIncertVoucherAccountings();

      // Check booking availabilities
      syncBookingAvailabilities(booking);

      // meausre addToBasket performance
      const operationEndTime = performance.now();
      const operationDuration = operationEndTime - operationStartTime;
      recordOperationDuration("add_to_basket", operationDuration);

      this.isLoading = false;
      this.dialogVisible = false;
      hideDialog();
      await this.$nextTick();
      if (this.showSuccessMessage) {
        NotificationService.showCustomSuccess(this.t("products.tickets-added"));
      }
    },
    editPrice(priceId: number) {
      this.editingPrice = priceId;
      // show calculator for the user to enter the new price (show the old price as a placeholder in the calculator)
      this.showNewPriceCalculator = true;
      const editedPrice = this.getEditedPriceValue(priceId);
      this.pricePlaceHolder =
        editedPrice !== null
          ? editedPrice / 100
          : this.prices[priceId]
          ? this.prices[priceId].value / 100
          : 0;
      // ModalModule.setStatus(ModalStatus.Calculator);
    },
    saveNewPrice(priceId: number, newPrice: number) {
      const existingIndex = this.editedPrices.findIndex(
        (price: any) => price.priceId === priceId
      );
      // do nothing if the new price has the same value as the original value
      if (this.prices[priceId] && this.prices[priceId].value === newPrice) {
        if (existingIndex !== -1) {
          this.editedPrices.splice(existingIndex, 1);
        }
        this.editingPrice = null;
        return;
      }
      if (existingIndex !== -1) {
        this.editedPrices[existingIndex].newPriceValue = newPrice;
      } else {
        this.editedPrices.push({
          priceId: priceId,
          oldPriceValue: this.prices[priceId].value,
          newPriceValue: newPrice,
        });
      }
      this.editingPrice = null;
    },
    handleCalculatorClick(newPrice: number) {
      if (!this.editingPrice) {
        return;
      }
      // If the new price has 3 decimal digits or more, we round it to be using only 2 decimal digits
      const formattedValue = Math.round(newPrice * 100);
      this.saveNewPrice(this.editingPrice, formattedValue);
      this.hideCalculator();
    },
    hideCalculator() {
      this.showNewPriceCalculator = false;
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.pc-select {
  $button-font-size: 22px;
  $button-height: 65px;

  // .image-wrapper {
  //   height: v-bind("pcselectPictureSize.height");
  //   width: v-bind("pcselectPictureSize.width");
  //   border-radius: 6px;
  //   border: 2px solid $color-light-grey3;
  //   background-color: white;
  //   // Making the image wrapper a flex to center the image in it
  //   display: flex;
  //   flex-direction: column;
  //   // center it vertically if the image is a landscape
  //   justify-content: center;
  //   // center it horizentally if the image is a portrait
  //   align-content: center;

  //   img {
  //     max-height: v-bind("pcselectPictureSize.height");
  //     max-width: v-bind("pcselectPictureSize.width");
  //   }
  // }

  img {
    height: v-bind("pcselectPictureSize.height");
    width: v-bind("pcselectPictureSize.width");
    object-fit: cover;
  }

  .key {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .value {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 400;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .is-fullwidth {
    width: 100%;
    border: none;
  }

  .price-select-wrapper {
    max-height: 450px;
    overflow-y: auto;
  }

  // .grid-content {
  .price-selector {
    // padding: 20px 0 0 7px;
    padding: 8px 5px;
    border-radius: 4px;
    min-height: 8vh;
    background: $color-light-grey3;
    margin-bottom: 15px;
    color: black;

    .price-name-wrapper {
      font-style: normal;
      text-align: left;
      letter-spacing: 0px;
      font-weight: 400;
      font-size: 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .price-name {
        font-weight: 700;
        font-size: 24px;
        line-height: 29px;
        margin-bottom: 8px;
      }
      .price-description {
        word-break: break-word;
      }
      .see-more {
        color: $color-green;
        cursor: pointer;
      }
    }

    .price-accounting {
      font-family: Arial, sans-serif, Arial;
      font-weight: 400;
      font-style: normal;
      font-size: 22px;
      text-align: center;
      line-height: 27px;
      letter-spacing: 0px;
    }

    .price-value-container {
      border: 1px solid $color-light-grey5;
      border-radius: 4px;
      .editable {
        margin-right: 5px;
        padding: 10px;
        border-radius: 4px 0px 0px 4px;
        background-color: $color-light-grey4;
        width: 70%;
      }

      .edit-price-button {
        margin-right: 5px;
        cursor: pointer;
      }
    }

    .price-count {
      background: white;
      min-width: 100px;
      min-height: $button-height;
      font-size: $button-font-size;
      text-align: center;
      border: none;
      outline: none;
      cursor: pointer;
    }
    .input-error {
      text-align: center;
      color: red;
    }
    .price-button {
      font-size: $button-font-size;
      height: $button-height;
      min-width: 110px;
      width: 110px;
      background-color: $color-green;
    }
    .transparent {
      opacity: 0.5;
    }
    .max-quantity-reached {
      line-height: 14px;
      font-size: 12px;
      text-align: center;
    }
  }

  .dialog-footer {
    button {
      width: 25%;
    }
  }

  .price-list-divider {
    margin: 0 0 10px;
  }

  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input[type="number"],
  input[type="number"]:hover,
  input[type="number"]:focus {
    -moz-appearance: textfield;
    appearance: none;
  }
}
</style>
